// The fal-key plans' launch price, in one place: every surface that shows a fal-key plan price
// (the /pricing cards and table, /bar and its JSON-LD, the plan cards in /select-plan and fal
// setup, the trial step) reads it from here. The launch price is what the products charge
// (docs/byok-billing-plan.md, decision 13), so the copy says "launch price", never "was $45".
// Plain functions (no 'use client'), so server components can read it too; the client hook that
// reads the switch from the URL is useLaunchOffer.
import type { ByokPlan } from './byokPlanData'

/**
 * `?offer=off` shows the regular prices instead of the launch prices: the one switch. Surfaces
 * rendered on the server (/bar, its JSON-LD) can't see the query and keep the launch price.
 */
export function isLaunchOfferOn(search: string): boolean {
  return new URLSearchParams(search).get('offer') !== 'off'
}

export interface ByokPlanPrice {
  /** The price a month to show. */
  price: string
  /** The regular price, struck through beside a launch price. Absent when `price` is the regular one. */
  regular?: string
}

/** What a plan costs a month, monthly or billed yearly, with or without the launch price. */
export function byokPlanPrice(
  plan: ByokPlan,
  { offer = true, yearly = false }: { offer?: boolean; yearly?: boolean } = {},
): ByokPlanPrice {
  const regular = yearly && plan.annual ? plan.annual : plan.price
  if (offer && plan.founding) return { price: plan.founding[yearly ? 1 : 0], regular }
  return { price: regular }
}

/**
 * What an annual plan charges for the year: twelve months at its annual price a month ($15 → $180).
 * Absent on plans with no annual price (Free, Enterprise).
 */
export function byokPlanYearlyTotal(plan: ByokPlan, { offer = true }: { offer?: boolean } = {}): string | undefined {
  if (!plan.annual) return undefined
  const perMonth = Number(byokPlanPrice(plan, { offer, yearly: true }).price.replace(/[$,]/g, ''))
  return `$${(perMonth * 12).toLocaleString('en-US')}`
}

/** The line under a fal-key plan's price: which price it is and, on annual, that it is billed yearly. */
export function byokPriceNote(
  plan: ByokPlan,
  { offer = true, yearly = false }: { offer?: boolean; yearly?: boolean } = {},
): string {
  if (byokPlanPrice(plan, { offer, yearly }).regular) return yearly ? 'Launch price, billed yearly' : 'Launch price'
  return yearly && plan.annual ? `Billed yearly · ${plan.price} monthly` : ''
}

/**
 * The day a one-month trial started on `start` ends and the card is first charged: the same day
 * next month, or that month's last day when it is shorter (a trial started on 31 January is
 * charged on the last day of February).
 */
export function trialFirstChargeDate(start: Date): Date {
  const firstOfNextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1)
  const lastDay = new Date(firstOfNextMonth.getFullYear(), firstOfNextMonth.getMonth() + 1, 0).getDate()
  return new Date(firstOfNextMonth.getFullYear(), firstOfNextMonth.getMonth(), Math.min(start.getDate(), lastDay))
}
